import { Subscribable } from '@benzinga/subscribable';
import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExpressionObject, Session } from '@benzinga/session';
import { InternalNewsRequest } from './request';
import { SimpleNewsQuery, SimpleNewsOptions, TidParam } from './entities';
import { StoryObject, NewsfeedFieldType } from '@benzinga/advanced-news-manager';

interface ErrorEvent {
  error?: SafeError;
  errorType: `news:nodeId_query_returned_more_then_one_story`;
  type: 'error';
}

export type InternalNewsManagerEvent = ErrorEvent;

export class InternalNewsManager extends Subscribable<InternalNewsManagerEvent> {
  session: Session;
  private request: InternalNewsRequest;

  constructor(session: Session) {
    super();
    this.session = session;
    this.request = new InternalNewsRequest(session);
  }

  public static getName = () => 'benzinga-internal-news';

  getStories = async (
    expression: ExpressionObject<NewsfeedFieldType> | null,
    options?: SimpleNewsOptions,
    query?: SimpleNewsQuery,
  ): SafePromise<StoryObject[]> => {
    return await this.request.getStories(expression, options, query);
  };

  getStoriesWithTickers = async (
    tickers: string | string[],
    options?: SimpleNewsOptions,
  ): SafePromise<StoryObject[]> => {
    return await this.request.getStoriesWithTickers(tickers, options);
  };

  getStoriesWithPrimaryTicker = async (
    tickers: string | string[],
    options?: SimpleNewsOptions,
  ): SafePromise<StoryObject[]> => {
    return await this.request.getStoriesWithPrimaryTicker(tickers, options);
  };

  getStoriesWithTags = async (tags: TidParam, options?: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    return await this.request.getStoriesWithTags(tags, options);
  };

  getStoriesWithChannels = async (channels: TidParam, options?: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    return await this.request.getStoriesWithChannels(channels, options);
  };

  getStoriesWithChannelsAndTags = async (
    channels: TidParam,
    tags: TidParam,
    options?: SimpleNewsOptions,
  ): SafePromise<StoryObject[]> => {
    return await this.request.getStoriesWithChannelsAndTags(channels, tags, options, 'and');
  };

  getStoriesWithChannelsOrTags = async (channels: TidParam, tags: TidParam, options?: SimpleNewsOptions) => {
    return await this.request.getStoriesWithChannelsAndTags(channels, tags, options, 'or');
  };

  // DOESN'T WORK CURRENTLY
  getStoriesWithIds = async (ids: number[]): SafePromise<StoryObject[]> => {
    return await this.request.getStoriesWithIds(ids);
  };

  queryTopStories = async (
    query: ExpressionObject<NewsfeedFieldType>,
    options?: SimpleNewsOptions,
  ): SafePromise<StoryObject[]> => {
    return await this.request.queryTopStories(query, options);
  };

  querySearch = async (query: SimpleNewsQuery, options?: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    return await this.request.querySearch(query, options);
  };

  simplyQueryNews = async (query: SimpleNewsQuery, options: SimpleNewsOptions): SafePromise<StoryObject[]> => {
    return await this.request.simplyQueryNews(query, options);
  };
}
